import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="asset-rules"
//
// The Assets from Plans slideover (decision-83). Picking "New product…" in a
// door kind's select opens the stacked Common::Dialog (a sibling of the rules
// form) to name the product; "Use This Product" POSTs it, and the turbo-stream
// answer puts the new product in the select. Cancelling puts the select back
// where it was. Picking a product ticks the row's "create assets" box, and
// "No product" clears it.
export default class extends Controller {
  // copyId: the team's own copy of the Fire Door library master, if it has one
  // — "from the library" then starts as a copy of THAT (it carries the
  // inspection forms the master lacks).
  static values = { copyId: String }
  static targets = [ "product", "enabled", "productForm", "kindField", "nameField", "source", "copyFrom" ]

  connect() {
    this.pendingKind = null
    this.created = false
    this.onClose = () => this.revertIfCancelled()
    this.dialog?.addEventListener("close", this.onClose)
  }

  disconnect() {
    this.dialog?.removeEventListener("close", this.onClose)
  }

  get dialog() {
    return this.element.querySelector("dialog")
  }

  productChanged(event) {
    const select = event.target
    const value = select.value

    if (value.startsWith("new:")) {
      this.openDialog(select.dataset.kind, value.slice(4))
      return
    }

    select.dataset.previous = value
    this.setEnabled(select.dataset.kind, value !== "")
  }

  openDialog(kind, source) {
    this.pendingKind = kind
    this.created = false
    this.kindFieldTarget.value = kind
    this.nameFieldTarget.value = `${this.titleCase(kind)} ${source === "blank" ? "Door" : "Fire Door"}`
    const useCopy = source === "library" && this.copyIdValue !== ""
    const wanted = useCopy ? "copy" : source
    this.sourceTargets.forEach((radio) => { radio.checked = radio.value === wanted && !radio.disabled })
    if (useCopy) this.copyFromTarget.value = this.copyIdValue
    this.sourceChanged()
    this.dialog.showModal()
    this.nameFieldTarget.focus()
    this.nameFieldTarget.select()
  }

  sourceChanged() {
    const copy = this.sourceTargets.find((radio) => radio.value === "copy")
    this.copyFromTarget.disabled = !(copy && copy.checked)
  }

  // turbo:submit-end on the dialog's form.
  productSubmitted(event) {
    if (!event.detail.success) return

    this.created = true
    this.selectCreated(event)
    this.setEnabled(this.pendingKind, true)
    this.dialog.close()
  }

  // The turbo-stream answer replaces the row's select with the new product
  // selected; this makes sure of it from the response header too, so the row
  // can never be left reading "New product…" (a Save would then make a second).
  selectCreated(event) {
    const id = event.detail.fetchResponse?.response?.headers?.get("X-Asset-Rules-Product-Id")
    const select = this.productTargets.find((el) => el.dataset.kind === this.pendingKind)
    if (!id || !select) return

    if (![ ...select.options ].some((option) => option.value === id)) return

    select.value = id
    select.dataset.previous = id
  }

  revertIfCancelled() {
    if (this.created || !this.pendingKind) return

    const select = this.productTargets.find((el) => el.dataset.kind === this.pendingKind)
    if (select) select.value = select.dataset.previous || ""
    this.pendingKind = null
  }

  setEnabled(kind, on) {
    const box = this.enabledTargets.find((el) => el.dataset.kind === kind)
    if (box) box.checked = on
  }

  titleCase(kind) {
    return kind.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
  }
}
